"use client";
import React, { useState } from "react";
import Image from "next/image";
import Link from "next/link";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowRight, Play, X } from "lucide-react";
import { useT } from "@/contexts/LanguageContext";
import useBodyScrollLock from "@/hooks/useBodyScrollLock";

export default function Hero() {
  const [isVideoOpen, setIsVideoOpen] = useState(false);
  const t = useT();

  /* The overlay is fixed, so without this the wheel keeps reaching the page
     behind it and the hero scrolls away under the open video. */
  useBodyScrollLock(isVideoOpen);

  return (
    <section
      className="relative w-full min-h-screen lg:min-h-[100svh] lg:h-[100svh]
                 overflow-hidden font-sans
                 flex items-center pt-12 sm:pt-16 lg:pt-[76px] pb-8 lg:pb-6"
    >
      {/* Hero background.
          An <Image>, not a CSS `background-image`. As a background it was the
          single heaviest thing on the site: a CSS url() never reaches the
          /_next/image optimizer, so every visitor — a 360px phone included —
          downloaded the full 1.36MB PNG at its native size. Routed through
          next/image the same art is re-encoded to AVIF/WebP and cut to the
          device width, which is ~1.3MB off a mobile page load.

          `fill` + object-cover reproduces `bg-cover bg-center` exactly.
          `priority` because it paints behind the LCP text; `sizes="100vw"`
          because the layer is always full-bleed. Default quality (75) is right
          here — this is a soft out-of-focus wash with no fine detail to
          protect, unlike the dashboard screenshot below. */}
      <div className="absolute inset-0 z-0 pointer-events-none">
        <Image
          src="/blog-banners/hero-section bg.png"
          alt=""
          aria-hidden
          fill
          priority
          sizes="100vw"
          className="object-cover object-center"
        />
      </div>
      <div className="absolute inset-0 z-0 bg-gradient-to-b from-white/10 via-transparent to-white/20 pointer-events-none" />

      {/* Main flex row — 100% responsive height layout with navbar clearance */}
      <div
        className="site-container relative z-10 flex flex-col lg:flex-row
                    items-center justify-between gap-10 lg:gap-6
                    py-8 sm:py-10 lg:py-6
                    w-full lg:h-full"
      >

        {/* ─── LEFT COLUMN ─── */}
        <div className="w-full lg:w-[44%] 2xl:w-[40%] flex flex-col flex-shrink-0">

          {/* Headline.
              Fluid from lg up rather than stepped. The column this sits in is a
              percentage of the container (44%, 40% at 2xl), so a fixed size per
              breakpoint is measured against a moving target: the old
              lg:58px / xl:56px handed the *narrowest* desktop the *largest*
              headline. "Stronger Connections." needs roughly 10px of width per
              1px of font — ~560px at 56px — against a column only 415px wide at
              1024, so it wrapped to five lines there and four at 1280, which is
              what pushed this column to 591px tall while the mockups beside it
              were 235px. The slope holds font ≈ column/10.4 across the band and
              the 56px ceiling lands at ~1404px, so every width at or above that
              — including 2xl, where the column narrows to 40% — renders exactly
              as it did before. */}
          {/* Plain <h1> with a CSS entry animation, NOT motion.h1. This is the
              LCP element of the site: framer-motion writes its `initial` state
              into the server-rendered HTML, so as a motion component this
              headline shipped at `opacity: 0` and stayed invisible until the JS
              bundle downloaded and hydrated — LCP 6.6s against FCP 1.8s on
              throttled mobile. `.hero-rise` reproduces the same 0.5s rise from
              CSS, which runs at first paint with no JS involved. See the
              keyframes note in landing.css before changing either. */}
          <h1
            className="hero-rise hero-rise-1 font-heading font-light text-[37px] min-[380px]:text-[42px] sm:text-[54px] lg:text-[clamp(40px,calc(4.2vw_-_3.4px),56px)] leading-[1.12] sm:leading-[1.08] text-[#0F172A] tracking-[-0.04em] mb-5 sm:mb-6"
          >
            {t("hero.titleLine1")}<br />
            {t("hero.titleLine2")}<br />
            <span className="font-extrabold text-transparent bg-clip-text bg-gradient-to-r from-[#1E3BB3] via-[#3D5EE1] to-[#60A5FA]">
              {t("hero.titleAccent")}
            </span>
          </h1>

          {/* Subtitle.
              An <h2>, not a <p>: the H1 above is a brand slogan and carries
              none of the terms this page ranks for, so the strongest remaining
              heading needs to say what the product actually is. Renders
              pixel-identically — `.landing-root h2` resolves to the same
              family the paragraph inherited, and index.css pins h1-h6 to the
              same weight 300 the body already had. */}
          {/* CSS-animated for the same reason as the h1 above — it is the
              second-largest text block in the viewport, so it is the LCP
              candidate the moment the headline stops being one. Delay 0.2s
              keeps the original stagger against the h1's 0.1s. */}
          <h2
            className="hero-rise hero-rise-2 text-[16.5px] sm:text-[20px] text-slate-600 max-w-[580px] leading-[1.6] tracking-[-0.005em] mb-8 sm:mb-10 text-balance"
          >
            {t("hero.subtitle")}
          </h2>

          {/* CTAs */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.3 }}
            className="flex flex-wrap items-center gap-3.5"
          >
            {/* Primary — gradient pill with inner highlight + sheen sweep */}
            <Link
              href="/contact"
              className="group relative isolate overflow-hidden inline-flex items-center gap-2.5
                         pl-7 sm:pl-8 pr-2 sm:pr-2.5 py-2 rounded-full
                         text-[15px] sm:text-[16.5px] font-semibold tracking-[-0.01em] text-white
                         bg-[linear-gradient(180deg,#3D5EE1_0%,#2A48C9_55%,#1E3BB3_100%)]
                         shadow-[0_1px_0_0_rgba(255,255,255,0.28)_inset,0_10px_24px_-8px_rgba(61,94,225,0.65),0_2px_6px_-2px_rgba(15,23,42,0.18)]
                         transition-[transform,box-shadow] duration-300 ease-out
                         hover:-translate-y-[3px]
                         hover:shadow-[0_1px_0_0_rgba(255,255,255,0.32)_inset,0_18px_36px_-10px_rgba(61,94,225,0.7),0_3px_8px_-2px_rgba(15,23,42,0.2)]
                         active:translate-y-0 active:scale-[0.97] active:duration-100
                         focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500/60 focus-visible:ring-offset-2"
            >
              {/* sheen */}
              <span
                aria-hidden
                className="pointer-events-none absolute inset-0 -z-10 translate-x-[-120%] group-hover:translate-x-[120%]
                           transition-transform duration-[900ms] ease-[cubic-bezier(0.22,1,0.36,1)]
                           bg-[linear-gradient(105deg,transparent_35%,rgba(255,255,255,0.38)_50%,transparent_65%)]"
              />
              <span className="relative">{t("hero.ctaDemo")}</span>
              <span
                className="relative flex items-center justify-center w-9 h-9 rounded-full shrink-0
                           bg-white/18 ring-1 ring-white/25 backdrop-blur-[1px]
                           transition-colors duration-300 group-hover:bg-white/28"
              >
                <ArrowRight
                  size={16}
                  strokeWidth={2.5}
                  className="transition-transform duration-300 ease-out group-hover:translate-x-[3px] group-active:translate-x-[3px]"
                />
              </span>
            </Link>

            {/* Secondary — frosted glass pill */}
            <button
              onClick={() => setIsVideoOpen(true)}
              className="group relative inline-flex items-center gap-3
                         pl-2 sm:pl-2.5 pr-6 sm:pr-7 py-2 rounded-full cursor-pointer
                         text-[15px] sm:text-[16.5px] font-semibold tracking-[-0.01em] text-slate-800
                         bg-white/70 hover:bg-white/90 backdrop-blur-md
                         ring-1 ring-slate-900/8 hover:ring-slate-900/12
                         shadow-[0_1px_0_0_rgba(255,255,255,0.9)_inset,0_6px_18px_-8px_rgba(15,23,42,0.28)]
                         transition-[transform,box-shadow,background-color] duration-300 ease-out
                         hover:-translate-y-[3px]
                         hover:shadow-[0_1px_0_0_rgba(255,255,255,0.9)_inset,0_14px_30px_-10px_rgba(15,23,42,0.3)]
                         active:translate-y-0 active:scale-[0.97] active:duration-100
                         focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500/50 focus-visible:ring-offset-2"
            >
              <span
                className="relative flex items-center justify-center w-9 h-9 rounded-full shrink-0
                           bg-gradient-to-b from-slate-900 to-slate-700 text-white
                           shadow-[0_2px_6px_-2px_rgba(15,23,42,0.5)]
                           transition-transform duration-300 ease-out group-hover:scale-[1.06] group-active:scale-[1.06]"
              >
                {/* subtle pulse ring on hover */}
                <span
                  aria-hidden
                  className="absolute inset-0 rounded-full ring-2 ring-slate-900/15 opacity-0 scale-100
                             group-hover:opacity-100 group-hover:scale-[1.35] transition-all duration-500 ease-out"
                />
                <Play size={11} className="fill-current ml-[1.5px]" />
              </span>
              {t("hero.ctaWatch")}
            </button>
          </motion.div>


        </div>{/* end left column */}

        {/* ─── RIGHT COLUMN ─── */}
        {/* Both mockups are real product captures from public/screenshots — the
            same set the /features catalogue uses (see featureScreenshots.ts).
            They replaced hand-built DOM replicas that had to be re-styled by
            hand every time the actual admin UI moved. */}
        {/* One row, one scale. The phone takes a flat 25% of this column and the
            card takes the rest, so the two are locked to a single number at
            every width and the composition can only scale, never re-proportion.
            That is the whole point of the rewrite: the phone used to be sized
            off the *viewport* (15.7vw wide, 34vw tall, clamped) while the card
            was sized off whatever width was *left over* after a matching
            padding reservation on this column. The two scales tracked each
            other only at the extremes. Between roughly 1024 and 1500 they came
            apart badly — at 1430 the phone measured 222x480 against a 502x352
            card and stood 129px proud of its top edge, reading as a giant phone
            next to a shrunken dashboard. With a fixed share that overhang is
            constant instead: 25% puts the phone ~2.9% of the column taller than
            the card, i.e. a deliberate ~17-20px, enough for the front layer to
            sit slightly proud without looming.

            The devices sit side by side rather than overlapping — hence the
            8px gap rather than a negative offset. The hand-built mockup this
            replaced carried a 30px empty gutter down its sidebar and could
            afford to be overlapped; the real capture puts its Kaympus wordmark
            ~16px from the screen edge and renders at roughly a third of the
            1440px original, so even 5px of overlap swallows the "K". The phone
            still reads as the front layer — z-20, and it drops a shadow across
            the card.

            flex-row-reverse, so DOM order stays card-then-phone (the card is
            the LCP image and should start decoding first) while the phone still
            paints on the left. Both are in normal flow now; the phone used to
            be absolute so it contributed no height, which in turn needed a
            min-h reservation on this column to stop it spilling up over the
            CTAs. Neither is necessary once it is a flex item.

            Below `sm` the side-by-side stops working at all: the card would get
            a ~228px strip on a 375px screen and render as an unreadable sliver.
            There the card is dropped entirely and the phone becomes an ordinary
            centred flex child at its own standalone size.

            max-w caps this column by *height* at lg, where the section is a
            fixed 100svh. Composition height is 0.541x its width, so the inverse
            is 1.85x the height available after the section's 76px navbar
            clearance and 72px of stacked padding (160px, rounded up for
            margin). It is inert at ordinary window shapes — it needs a viewport
            wider than ~1.85x its usable height to bind — and only engages on
            short, wide windows, where it scales the pair down as one instead of
            letting it overflow the section. */}
        <div
          className="w-full lg:flex-1 lg:min-w-0 relative
                     flex items-end justify-center sm:flex-row-reverse sm:gap-2
                     mt-4 lg:mt-0 pb-8 lg:pb-0
                     lg:max-w-[calc((100svh_-_160px)_*_1.85)]"
        >

          {/* Dashboard card */}
          {/* The idle bob is a CSS animation (`lp-hero-float`), not a
              framer-motion `repeat: Infinity`. Both look the same; only one is
              free. A JS-driven infinite animation keeps framer-motion's rAF
              loop alive for as long as the page is open, waking the main
              thread every frame to write a transform — and it does that
              whether or not the hero is still on screen, so it was still
              running while the visitor read the FAQ. The CSS version is
              handed to the compositor and never touches the main thread at
              all, which is exactly what you want on a phone where that thread
              is also trying to service the visitor's finger. The entrance
              animation stays in framer-motion: it runs once and stops.

              They have to be two elements. Both animate `transform`, and a CSS
              animation outranks an inline style in the cascade — put them on
              one node and the keyframes silently eat the entrance slide. So
              the outer node owns the loop (and the flex sizing, since it is
              the flex item now), the inner one owns the entrance and all the
              visual treatment. Period is preserved via the inline
              animation-duration; see the phone below for why the two are
              deliberately out of step. `motion-reduce:animate-none` is
              belt-and-braces over landing.css's reduced-motion block. */}
          <div
            className="lp-hero-float motion-reduce:animate-none
                       hidden sm:block sm:flex-1 sm:min-w-0 z-10"
            // 7.2s, not the 3.6s that was written here in framer-motion: that
            // was a `repeatType: "mirror"` duration, i.e. the time for ONE
            // traverse, so the round trip was twice it. A CSS keyframe cycle
            // is the whole round trip, so matching the original speed means
            // doubling the number.
            style={{ animationDuration: "7.2s" }}
          >
          <motion.div
            initial={{ opacity: 0, x: 40, scale: 0.97 }}
            animate={{ opacity: 1, x: 0, scale: 1 }}
            transition={{ duration: 1.1, ease: [0.22, 1, 0.36, 1] }}
            className="relative bg-white rounded-[22px] sm:rounded-[28px]
                       shadow-[0_24px_70px_-10px_rgba(61,94,225,0.22)]
                       border border-slate-200/80 overflow-hidden"
            style={{ aspectRatio: "1220/855" }}
          >
            {/* Its own file, not the admin/dashboard.webp the /features
                catalogue serves. It is that 1440×900 capture cut to 1220×855 —
                two cuts, for two different reasons.

                x≥220 drops the app's left nav. That sidebar is the same menu
                tree this site deliberately stopped listing as text, and a hero
                shot does not need to re-publish it; FeatureCatalogue crops its
                own captures the same way and for the same reason. It has to
                happen in the file, not in CSS — a CSS crop still ships the
                whole image to anyone who opens it in a new tab. 220 is the
                first content column: the divider rule sits at x=219.

                y<855 drops the footer. The admin shell pins it to the bottom of
                the viewport, so rows 855-900 of every capture under
                public/screenshots are a white "Copyright © 2026 - Maple Ridge
                Public School" band sitting *on top of* content that scrolls on
                below it. At hero size that band lands mid-composition and
                slices the earnings chart and the Quick Links tiles in half — it
                reads as a broken image rather than a fold.

                It is stored lossless, and quality={92} overrides the optimizer's
                default 75 (see images.qualities in next.config.mjs). A UI
                capture is the worst case for a lossy encoder — flat panels cost
                nothing, but the 1px text and hairline card borders that are
                most of a dashboard are what it discards first — and this one
                was being compressed three times over: the original .webp, then
                this crop, then next/image. Lossless here removes the middle
                pass; 92 nearly removes the last. The 195KB on disk never
                reaches a browser, only the optimizer reads it.

                The dimensions are in the filename on purpose. Browsers, the
                /_next/image optimizer and the CDN all key on the URL alone, and
                this asset has twice been re-cut in place and then served stale
                at the old shape — first as dashboard.webp (1220×900 sidebar-
                cropped, then 1440×900 uncropped), then again as a plain
                dashboard-hero.webp. Since aspectRatio below is hard-coded to
                one shape, a silent shape change is exactly the failure that
                hurts. Re-cutting to any other size now forces a new URL, so the
                two can never drift apart. The -v2 covers the other half: a
                re-encode at the *same* dimensions can't be caught by the size
                in the name, and would otherwise ship invisibly-stale bytes.
                Between them the rule is simply never overwrite this in place —
                change the name, and delete the file you replaced.

                aspectRatio therefore matches the file exactly and object-contain
                — not cover — does the fitting, so should this asset ever be
                swapped for one of another shape the worst case is a letterbox
                bar, never silently cropped-away content. */}
            <Image
              src="/screenshots/admin/dashboard-hero-1220x855-v2.webp"
              alt={t("hero.dashboardAlt")}
              fill
              priority
              quality={92}
              sizes="(min-width: 1720px) 730px, (min-width: 1024px) 43vw, (min-width: 640px) 76vw, 92vw"
              className="object-contain object-top"
            />

            {/* Even cut at 855 the capture ends mid-card — the page scrolls on
                below the viewport it was shot in, so the earnings chart and the
                second row of Quick Links tiles are sliced by the image edge.
                Nothing can un-slice them short of a re-capture at a taller
                viewport, so the bottom ~11% dissolves into the page instead:
                the dashboard's background is pure #fff edge to edge, so a fade
                to white is seamless and reads as an intentional fold. It starts
                at row ~761, just under the first row of Quick Links labels,
                leaving everything above it crisp. */}
            <div
              aria-hidden
              className="pointer-events-none absolute inset-x-0 bottom-0 h-[11%]
                         bg-gradient-to-b from-transparent via-white/70 to-white"
            />
          </motion.div>
          </div>{/* end float wrapper */}

          {/* Phone mockup */}
          {/* `relative` keeps `fill` on the screenshot anchored to this frame
              rather than to the section. Width is the only size given at each
              breakpoint — aspect-ratio derives the height from the 390:844 of
              the capture, so nothing can be cropped and there is no second
              number to keep in sync. Standing alone below sm it can afford to
              be far larger than the 25% share it takes at sm+, where it is only
              the companion to the dashboard card.

              shrink-0 is belt-and-braces: the card is the only flex item with a
              grow factor and the row never overflows, so nothing should shrink
              this — but a percentage basis next to a flex-1 sibling is exactly
              the shape that silently collapses if either ever gains a min-width.

              Its float is near-symmetric around 0, not the [-6,-16] it used to
              be: an always-negative drift parks the phone ~11px above wherever
              the layout puts it, which re-opens the baseline gap items-end
              exists to close. Sharing the card's `lp-hero-float` keyframes
              (-4px to +6px) keeps the two bottoms level on average, and the
              6.8s vs 7.2s periods still drift them out of phase so they never
              bob in lockstep. */}
          {/* Split into loop-wrapper + entrance for the same cascade reason as
              the card above. This is the one that mattered most on mobile: the
              card is `hidden` below sm, so on a phone this was the *only* hero
              device on screen — and it was the element keeping framer-motion's
              rAF loop running for the life of the page. */}
          <div
            className="lp-hero-float motion-reduce:animate-none shrink-0
                       w-[min(56vw,213px)] sm:w-[25%] z-20"
            // 6.8s for the same reason as the card's 7.2s — a mirrored
            // framer duration is a half cycle. The two stay deliberately
            // mismatched so the pair never bobs in lockstep.
            style={{ animationDuration: "6.8s", animationDelay: "0.25s" }}
          >
          <motion.div
            initial={{ opacity: 0, scale: 0.96 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 1.1, delay: 0.25, ease: [0.22, 1, 0.36, 1] }}
            className="relative w-full
                       bg-white rounded-[1.6rem] sm:rounded-[2.2rem]
                       shadow-[0_25px_60px_-10px_rgba(0,0,0,0.3)]
                       border-[5px] sm:border-[7px] border-[#0F172A]
                       overflow-hidden ring-2 ring-white/80"
            style={{ aspectRatio: "390/844" }}
          >
            {/* The 390×844 capture carries the device's own status bar and tab
                bar, so the frame adds only the bezel — no synthetic chrome. */}
            <Image
              src="/screenshots/mobile/student-home.webp"
              alt={t("hero.phoneAlt")}
              fill
              priority
              sizes="(min-width: 640px) 240px, 213px"
              className="object-cover"
            />
          </motion.div>
          </div>{/* end float wrapper */}

        </div>{/* end right column */}

      </div>

      {/* ── Video Overview Modal ── */}
      <AnimatePresence>
        {isVideoOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[70] flex items-center justify-center p-4 sm:p-6 bg-slate-900/80 backdrop-blur-md"
            onClick={() => setIsVideoOpen(false)}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              transition={{ type: "spring", damping: 25, stiffness: 300 }}
              className="relative w-full max-w-4xl bg-slate-950 rounded-2xl sm:rounded-3xl overflow-hidden shadow-2xl border border-slate-800 flex flex-col"
              onClick={(e) => e.stopPropagation()}
            >
              {/* Modal Top Bar */}
              <div className="flex items-center justify-between px-5 py-3.5 bg-slate-900 border-b border-slate-800">
                <div className="flex items-center gap-2.5">
                  <div className="w-6 h-6 rounded-full bg-[#3D5EE1]/20 flex items-center justify-center">
                    <Play size={12} className="text-[#93B4FF] fill-current ml-0.5" />
                  </div>
                  <span className="text-sm font-bold text-white tracking-tight">{t("hero.modalTitle")}</span>
                </div>
                <button
                  onClick={() => setIsVideoOpen(false)}
                  aria-label={t("hero.modalClose")}
                  className="w-8 h-8 rounded-full bg-slate-800 hover:bg-slate-700 text-slate-400 hover:text-white flex items-center justify-center transition-colors"
                >
                  <X size={16} />
                </button>
              </div>

              {/* Video Container — no overview video has been produced yet, so
                  this is a deliberate placeholder panel rather than a broken
                  embed (the previous YouTube ID had been taken down and showed
                  an error player). Swap in an <iframe> here when the real
                  video ships. */}
              <div className="relative w-full aspect-video bg-[radial-gradient(120%_120%_at_50%_0%,#131F3D_0%,#070B17_60%)] flex items-center justify-center">
                <div className="flex flex-col items-center text-center gap-4 px-6">
                  <div className="w-16 h-16 rounded-full bg-[#3D5EE1]/15 ring-1 ring-[#60A5FA]/30 flex items-center justify-center">
                    <Play size={22} className="text-[#93B4FF] fill-current ml-1" />
                  </div>
                  <div>
                    <p className="text-white font-bold text-lg sm:text-xl tracking-tight">{t("hero.modalHeading")}</p>
                    <p className="text-slate-400 text-[13px] sm:text-sm mt-1.5 max-w-[420px]">
                      {t("hero.modalBody")}
                    </p>
                  </div>
                  <Link
                    href="/contact"
                    className="inline-flex items-center gap-2 mt-1 px-6 py-3 rounded-full text-[14px] font-bold text-white
                               bg-[linear-gradient(180deg,#3D5EE1_0%,#2A48C9_55%,#1E3BB3_100%)]
                               shadow-[0_10px_24px_-8px_rgba(61,94,225,0.65)]
                               hover:-translate-y-0.5 transition-transform duration-200"
                  >
                    {t("hero.modalCta")}
                    <ArrowRight size={15} />
                  </Link>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
}
